import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { languages } from '../i18n';
import { BASE_URL as BASE } from '../config';

const api = async (path, body) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // 15-second timeout
  try {
    const r = await fetch(`${BASE}${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: controller.signal,
    });
    return r.json();
  } catch (err) {
    if (err.name === 'AbortError') throw new Error('Request timed out. Please check your connection.');
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
};

/* ── Input ─────────────────────────────────────────────────────── */
function Input({ label, type = 'text', value, onChange, placeholder, required, autoFocus, icon }) {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      {label && <label style={{ display: 'block', fontSize: '0.78rem', fontWeight: '600', color: '#374151', marginBottom: '0.4rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</label>}
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
        {icon && <span style={{ position: 'absolute', left: '13px', fontSize: '1rem', pointerEvents: 'none', color: focused ? '#002147' : '#9ca3af', transition: 'color 0.2s' }}>{icon}</span>}
        <input
          type={type} value={value} onChange={onChange} placeholder={placeholder}
          required={required} autoFocus={autoFocus}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          /* RESPONSIVE: 1rem = 16px minimum to prevent iOS auto-zoom on focus */
          style={{ width: '100%', padding: icon ? '0.7rem 0.75rem 0.7rem 2.6rem' : '0.7rem 0.75rem', border: `2px solid ${focused ? '#002147' : '#e5e7eb'}`, borderRadius: '10px', fontSize: '1rem', outline: 'none', transition: 'border-color 0.2s, box-shadow 0.2s', boxSizing: 'border-box', boxShadow: focused ? '0 0 0 3px rgba(0,33,71,0.08)' : 'none', background: 'white' }}
        />
      </div>
    </div>
  );
}

/* ── Button ────────────────────────────────────────────────────── */
function Btn({ children, type = 'button', onClick, loading, disabled }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading}
      /* RESPONSIVE: min-height 44px satisfies touch target requirement */
      style={{ width: '100%', padding: '0.8rem', minHeight: '44px', background: disabled || loading ? '#9ca3af' : '#002147', color: 'white', border: 'none', borderRadius: '10px', fontSize: '1rem', fontWeight: '700', cursor: disabled || loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'opacity 0.2s' }}
      onMouseEnter={e => { if (!disabled && !loading) e.currentTarget.style.opacity = '0.88'; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
    >
      {loading && <span style={{ width: '16px', height: '16px', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: 'white', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.7s linear infinite' }} />}
      {children}
    </button>
  );
}

/* ── OTP Input ─────────────────────────────────────────────────── */
function OTPInput({ value, onChange }) {
  const inputs = useRef([]);
  const digits = (value + '      ').slice(0, 6).split('');
  const handleKey = (i, e) => {
    const v = e.target.value.replace(/\D/g, '').slice(-1);
    const next = digits.map((d, idx) => idx === i ? v : d);
    onChange(next.join('').trim());
    if (v && i < 5) inputs.current[i + 1]?.focus();
    if (e.key === 'Backspace' && !v && i > 0) inputs.current[i - 1]?.focus();
  };
  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (paste) { onChange(paste); inputs.current[Math.min(paste.length, 5)]?.focus(); }
    e.preventDefault();
  };
  return (
    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', margin: '1.5rem 0' }}>
      {digits.map((d, i) => (
        <input key={i} ref={el => inputs.current[i] = el}
          type="text" inputMode="numeric" maxLength={1} value={d.trim()}
          onChange={e => handleKey(i, e)} onKeyDown={e => handleKey(i, e)} onPaste={handlePaste}
          /* RESPONSIVE: 1.4rem kept for desktop; iOS won't zoom since the base
             font is already >= 16px (1rem) set globally in globals.css */
          style={{ width: '48px', height: '56px', textAlign: 'center', fontSize: '1.4rem', fontWeight: '700', border: `2px solid ${d.trim() ? '#002147' : '#e5e7eb'}`, borderRadius: '10px', outline: 'none', transition: 'all 0.2s', background: d.trim() ? '#f0f4ff' : 'white', color: '#002147' }}
        />
      ))}
    </div>
  );
}

/* ── Error box ─────────────────────────────────────────────────── */
function ErrBox({ msg }) {
  if (!msg) return null;
  return <div style={{ padding: '0.65rem 0.8rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.83rem', color: '#dc2626' }}>⚠️ {msg}</div>;
}

/* ════════════════════════════════════════════════════════════════
   MAIN LOGIN PAGE
════════════════════════════════════════════════════════════════ */
export default function LoginPage() {
  const { lang, changeLang } = useLanguage();
  const [view, setView] = useState('login'); // login | forgot | otp | newpw | done
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  // If already authenticated, skip the login page entirely
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const role = (() => { try { return JSON.parse(userData).role; } catch { return null; } })();
      window.location.replace(
        role === 'MASTER_ADMIN' ? '/master' :
        role === 'TEACHER'      ? '/teacher' :
        role === 'PARENT'       ? '/parent'  : '/dashboard'
      );
    }
  }, []);

  useEffect(() => {
    if (otpCountdown > 0) {
      const t = setTimeout(() => setOtpCountdown(c => c - 1), 1000);
      return () => clearTimeout(t);
    }
  }, [otpCountdown]);

  /* ── Login submit ── */
  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const data = await api('/auth/login', { email, password });
      if (data.success) {
        localStorage.setItem('user', JSON.stringify(data.data.user));
        const role = data.data.user?.role;
        window.location.replace(role === 'MASTER_ADMIN' ? '/master' : role === 'TEACHER' ? '/teacher' : role === 'PARENT' ? '/parent' : '/dashboard');
      } else {
        setError(data.message || 'Invalid email or password');
      }
    } catch (err) { setError(err.message || 'Cannot connect to server'); }
    finally { setLoading(false); }
  };

  /* ── Forgot submit ── */
  const handleForgot = async (e) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const data = await api('/auth/forgot-password', { email });
      if (data.success) { setView('otp'); setOtpCountdown(60); }
      else setError(data.message || 'Error sending code');
    } catch (err) { setError(err.message || 'Cannot connect to server'); }
    finally { setLoading(false); }
  };

  /* ── OTP submit ── */
  const handleOtp = async (e) => {
    e.preventDefault();
    if (otpValue.length < 6) { setError('Enter all 6 digits'); return; }
    setError(''); setResetCode(otpValue); setView('newpw');
  };

  /* ── New password submit ── */
  const handleNewPw = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { setError('Passwords do not match'); return; }
    if (newPw.length < 6) { setError('Password must be at least 6 characters'); return; }
    setError(''); setLoading(true);
    try {
      const data = await api('/auth/reset-password', { email, code: resetCode, newPassword: newPw });
      if (data.success) setView('done');
      else setError(data.message || 'Error');
    } catch (err) { setError(err.message || 'Cannot connect to server'); }
    finally { setLoading(false); }
  };

  const pwStrength = newPw.length >= 10 ? 3 : newPw.length >= 6 ? 2 : newPw.length >= 1 ? 1 : 0;
  const strengthColors = ['#ef4444', '#f59e0b', '#10b981'];
  const strengthLabels = ['Weak', 'Fair', 'Strong'];

  return (
    <div translate="no" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #f8fafc 0%, #e8eef5 100%)', fontFamily: "'Inter','Segoe UI',Arial,sans-serif", padding: '1rem' }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
        .auth-card { animation: fadeUp 0.3s ease both; }
      `}</style>

      <div className="auth-card" style={{ background: 'white', borderRadius: '20px', boxShadow: '0 8px 48px rgba(0,33,71,0.12)', padding: '2.5rem 2.25rem', width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <img src="/genius-logo.png" alt="Genius English Courses" style={{ height: '64px', objectFit: 'contain', margin: '0 auto 0.75rem', display: 'block' }} />
        </div>

        {/* ── LOGIN VIEW ── */}
        {view === 'login' && (
          <>
            <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#111827', margin: '0 0 0.25rem' }}>Welcome back</h2>
            <p style={{ color: '#6b7280', fontSize: '0.88rem', margin: '0 0 1.75rem' }}>Sign in to your account</p>
            <form onSubmit={handleLogin}>
              <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus icon="✉️" />
              <Input label="Password" type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" required icon="🔒" />
              <div style={{ textAlign: 'right', marginTop: '-0.35rem', marginBottom: '1.25rem' }}>
                <button type="button" onClick={() => { setView('forgot'); setError(''); }}
                  style={{ background: 'none', border: 'none', color: '#002147', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '600', padding: 0 }}>
                  Forgot password?
                </button>
              </div>
              <ErrBox msg={error} />
              <Btn type="submit" loading={loading}>Sign In</Btn>
            </form>
          </>
        )}

        {/* ── FORGOT VIEW ── */}
        {view === 'forgot' && (
          <>
            <button onClick={() => { setView('login'); setError(''); }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.82rem', padding: 0, marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>← Back to login</button>
            <div style={{ width: '52px', height: '52px', background: '#f0f4ff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginBottom: '1rem' }}>🔑</div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#111827', margin: '0 0 0.25rem' }}>Reset password</h2>
            <p style={{ color: '#6b7280', fontSize: '0.88rem', margin: '0 0 1.5rem' }}>Enter your email and we'll send a reset code</p>
            <form onSubmit={handleForgot}>
              <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@example.com" required autoFocus icon="✉️" />
              <ErrBox msg={error} />
              <Btn type="submit" loading={loading}>Send Reset Code</Btn>
            </form>
          </>
        )}

        {/* ── OTP VIEW ── */}
        {view === 'otp' && (
          <>
            <button onClick={() => { setView('forgot'); setError(''); }} style={{ background: 'none', border: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '0.82rem', padding: 0, marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>← Back</button>
            <div style={{ width: '52px', height: '52px', background: '#f0f4ff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginBottom: '1rem' }}>📬</div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#111827', margin: '0 0 0.25rem' }}>Check your email</h2>
            <p style={{ color: '#6b7280', fontSize: '0.88rem', margin: 0 }}>We sent a 6-digit code to</p>
            <p style={{ color: '#002147', fontWeight: '600', fontSize: '0.9rem', margin: '0.1rem 0 0' }}>{email}</p>
            <form onSubmit={handleOtp}>
              <OTPInput value={otpValue} onChange={setOtpValue} />
              <ErrBox msg={error} />
              <Btn type="submit" disabled={otpValue.length < 6}>Verify Code</Btn>
            </form>
            <div style={{ textAlign: 'center', fontSize: '0.82rem', color: '#6b7280', marginTop: '1rem' }}>
              {otpCountdown > 0
                ? <span style={{ color: '#9ca3af' }}>Resend in {otpCountdown}s</span>
                : <button type="button" onClick={async () => { await api('/auth/forgot-password', { email }); setOtpCountdown(60); setOtpValue(''); }}
                    style={{ background: 'none', border: 'none', color: '#002147', fontWeight: '700', cursor: 'pointer', fontSize: '0.82rem' }}>Resend code</button>
              }
            </div>
          </>
        )}

        {/* ── NEW PASSWORD VIEW ── */}
        {view === 'newpw' && (
          <>
            <div style={{ width: '52px', height: '52px', background: '#f0f4ff', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.5rem', marginBottom: '1rem' }}>🔐</div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#111827', margin: '0 0 0.25rem' }}>New password</h2>
            <p style={{ color: '#6b7280', fontSize: '0.88rem', margin: '0 0 1.5rem' }}>Choose a strong password</p>
            <form onSubmit={handleNewPw}>
              <Input label="New Password" type="password" value={newPw} onChange={e => setNewPw(e.target.value)} placeholder="Min. 6 characters" required autoFocus icon="🔒" />
              {newPw && (
                <div style={{ marginTop: '-0.4rem', marginBottom: '0.75rem' }}>
                  <div style={{ display: 'flex', gap: '3px', marginBottom: '3px' }}>
                    {[1, 2, 3].map(i => <div key={i} style={{ flex: 1, height: '3px', borderRadius: '2px', background: pwStrength >= i ? strengthColors[pwStrength - 1] : '#e5e7eb', transition: 'background 0.3s' }} />)}
                  </div>
                  {pwStrength > 0 && <span style={{ fontSize: '0.72rem', color: strengthColors[pwStrength - 1], fontWeight: '600' }}>{strengthLabels[pwStrength - 1]}</span>}
                </div>
              )}
              <Input label="Confirm Password" type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} placeholder="Repeat password" required icon="🔒" />
              <ErrBox msg={error} />
              <Btn type="submit" loading={loading}>Set New Password</Btn>
            </form>
          </>
        )}

        {/* ── DONE VIEW ── */}
        {view === 'done' && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
            <h2 style={{ fontSize: '1.2rem', fontWeight: '700', color: '#111827', margin: '0 0 0.5rem' }}>Password updated!</h2>
            <p style={{ color: '#6b7280', fontSize: '0.88rem', margin: '0 0 2rem' }}>You can now sign in with your new password.</p>
            <Btn onClick={() => { setView('login'); setPassword(''); setError(''); }}>Sign In</Btn>
          </div>
        )}

        {/* Language switcher */}
        <div style={{ marginTop: '1.75rem', display: 'flex', justifyContent: 'center', gap: '6px' }}>
          {Object.values(languages).map(l => (
            <button key={l.lang} onClick={() => changeLang(l.lang)}
              style={{ padding: '3px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '700', background: lang === l.lang ? '#002147' : '#f3f4f6', color: lang === l.lang ? 'white' : '#9ca3af', transition: 'all 0.15s' }}>
              {l.langLabel}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
